import ApplicationController from './application_controller.js'
import RiskFormAPI from './risk_form/risk_form_api.js'
import RiskFormRenderer from './risk_form/risk_form_renderer.js'
import RiskFormUtils from './risk_form/risk_form_utils.js'

export default class RiskFormController extends ApplicationController {
  static targets = [
    "accessButton", 
    "questionsContainer", 
    "questionsForm", 
    "existingAnswersContainer", 
    "existingAnswersContent", 
    "riskSelectionContainer", 
    "editAnswersButton", 
    "selectedRiskLevelDisplay",
    "exportPdfButton",
    "deleteButton"
  ]

  // Vérifier si les boutons optionnels sont présents
  get hasEditButton() {
    return this.hasEditAnswersButtonTarget
  }

  get hasExportButton() {
    return this.hasExportPdfButtonTarget
  }

  connect() {
    console.log('🔗 RiskFormController connecté')
    this.api = new RiskFormAPI(RiskFormUtils.getClientId())
    this.renderer = new RiskFormRenderer()
    this.state = {
      firstRiskLevel: null,
      existingAnswersData: null
    }
    
    this.initialize()
  }

  // Initialisation
  async initialize() {
    console.log('🚀 Initialisation du RiskFormController')
    try {
      await this.restoreLastSelectedRiskLevel()
      await this.checkExistingAnswers()
      // checkExistingAnswers() décide automatiquement de l'affichage
      // Si des réponses existent, elles seront affichées
      // Sinon, la sélection du niveau de risque sera affichée
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation:', error)
      RiskFormUtils.handleError(error, 'initialisation')
      this.showRiskSelection()
    }
  }

  // Restaurer le dernier niveau de risque sélectionné
  async restoreLastSelectedRiskLevel() {
    try {
      const riskLevelId = await this.api.getLastRiskLevel()
      if (riskLevelId) {
        const select = document.getElementById('risk_level_select')
        if (RiskFormUtils.setSelectValue(select, riskLevelId)) {
          this.updateAccessButton()
        }
      }
    } catch (error) {
      console.log('Aucun niveau de risque sauvegardé ou erreur:', error)
    }
  }

  // Gestion du changement de niveau de risque
  riskLevelChanged(event) {
    this.updateAccessButton()
  }

  // Mettre à jour le bouton d'accès
  updateAccessButton() {
    const selectedValue = RiskFormUtils.getSelectedRiskLevel()
    const accessButton = this.accessButtonTarget
    
    if (selectedValue) {
      const hasRiskLevelChanged = this.state.firstRiskLevel && 
        this.state.firstRiskLevel.id.toString() !== selectedValue
      
      if (this.state.firstRiskLevel && !hasRiskLevelChanged) {
        this.hideAccessButton()
      } else {
        this.showAccessButton(hasRiskLevelChanged)
      }
    } else {
      this.hideAccessButton()
      this.hideQuestionsForm()
    }
  }

  showAccessButton(isRegenerate = false) {
    const accessButton = this.accessButtonTarget
    const buttonText = document.getElementById('access_button_text')
    
    accessButton.style.display = 'inline-block'
    accessButton.disabled = false
    buttonText.textContent = isRegenerate ? 'Régénérer le formulaire' : 'Générer le formulaire'
  }

  hideAccessButton() {
    const accessButton = this.accessButtonTarget
    accessButton.style.display = 'none'
    accessButton.disabled = true
  }

  // Accéder aux questions
  async accessQuestions(event) {
    event.preventDefault()
    
    const riskLevelId = RiskFormUtils.getSelectedRiskLevel()
    if (!riskLevelId) return

    try {
      this.showLoading()
      const questions = await this.api.getQuestions(riskLevelId)
      this.hideLoading()
      
      const hasExistingAnswers = questions.some(q => q.existing_answer)
      if (hasExistingAnswers) {
        this.hideAccessButton()
      }
      
      this.displayQuestions(questions)
    } catch (error) {
      this.hideLoading()
      RiskFormUtils.handleError(error, 'chargement des questions')
    }
  }

  // Afficher les questions
  displayQuestions(questions) {
    const container = this.questionsContainerTarget
    const form = this.questionsFormTarget

    form.innerHTML = this.renderer.renderQuestionsForm(questions)
    
    // Ajouter l'écouteur d'événement pour la soumission
    const formElement = document.getElementById('risk_questions_form')
    if (formElement) {
      formElement.addEventListener('submit', (e) => this.submitAnswers(e))
    }

    container.style.display = 'block'
  }

  // Soumettre les réponses
  async submitAnswers(event) {
    event.preventDefault()
    
    try {
      this.showLoading()
      const formData = RiskFormUtils.createFormData(event.target)
      
      // Ajouter le risk_level_id aux données du formulaire
      const riskLevelId = RiskFormUtils.getSelectedRiskLevel()
      if (riskLevelId) {
        formData.append('risk_level_id', riskLevelId)
      }
      
      const success = await this.api.saveAnswers(formData)
      
      if (success) {
        this.hideQuestionsForm()
        await this.checkExistingAnswers()
      } else {
        RiskFormUtils.showError('Erreur lors de l\'enregistrement')
      }
    } catch (error) {
      RiskFormUtils.handleError(error, 'sauvegarde')
    } finally {
      this.hideLoading()
    }
  }

  // Vérifier les réponses existantes
  async checkExistingAnswers(riskLevelId = null) {
    try {
      const riskLevelToCheck = riskLevelId || RiskFormUtils.getSelectedRiskLevel()
      const { hasAnswers, answersByRiskLevel, firstRiskLevel } = await this.api.getExistingAnswers(riskLevelToCheck)
      
      this.state.firstRiskLevel = firstRiskLevel
      this.state.existingAnswersData = answersByRiskLevel
      
      if (hasAnswers) {
        this.displayExistingAnswers(answersByRiskLevel, firstRiskLevel, riskLevelToCheck)
      } else {
        this.showRiskSelection()
      }
    } catch (error) {
      RiskFormUtils.handleError(error, 'vérification des réponses')
      this.showRiskSelection()
    }
  }

  // Afficher les réponses existantes
  displayExistingAnswers(answersByRiskLevel, firstRiskLevel, currentRiskLevelId) {
    const container = this.existingAnswersContainerTarget
    const content = this.existingAnswersContentTarget
    const riskLevelDisplay = this.selectedRiskLevelDisplayTarget

    const result = this.renderer.renderExistingAnswers(answersByRiskLevel, firstRiskLevel, currentRiskLevelId)
    
    if (result.riskLevelName) {
      riskLevelDisplay.innerHTML = `<div style="color: #96254E; padding: 0.5rem 0;">Niveau de risque du client : ${result.riskLevelName}</div>`
    }

    content.innerHTML = result.html
    container.style.display = 'block'
    this.riskSelectionContainerTarget.style.display = 'none'
    
    // Afficher les boutons en mode show
    if (this.hasEditButton) {
      this.editAnswersButtonTarget.style.display = 'inline-block'
    }
    if (this.hasExportButton) {
      this.exportPdfButtonTarget.style.display = 'inline-block'
    }
    if (this.hasDeleteButton) {
      this.deleteButtonTarget.style.display = 'inline-block'
    }
  }

  // Passer en mode édition
  showEditMode() {
    console.log('🔧 showEditMode appelé')
    console.log('🔍 État actuel:', this.state)
    
    this.existingAnswersContainerTarget.style.display = 'none'
    this.showRiskSelection()
    
    if (this.hasEditButton) {
      this.editAnswersButtonTarget.style.display = 'none'
    }
    
    if (this.hasExportButton) {
      this.exportPdfButtonTarget.style.display = 'none'
    }
    if (this.hasDeleteButton) {
      this.deleteButtonTarget.style.display = 'none'
    }
    
    // Essayer de charger les questions si un niveau de risque est disponible
    const select = document.getElementById('risk_level_select')
    let riskLevelToUse = this.state.firstRiskLevel
    
    console.log('🔍 Select element:', select)
    console.log('🔍 firstRiskLevel:', this.state.firstRiskLevel)
    console.log('🔍 existingAnswersData:', this.state.existingAnswersData)
    
    // Si on a des données existantes, essayer de récupérer le niveau de risque sélectionné
    if (select && select.value && this.state.existingAnswersData) {
      const selectedRiskLevel = this.state.existingAnswersData.find(riskLevelData => 
        riskLevelData.risk_level && riskLevelData.risk_level.id && 
        riskLevelData.risk_level.id.toString() === select.value.toString()
      )
      if (selectedRiskLevel) {
        riskLevelToUse = selectedRiskLevel.risk_level
        console.log('🔍 Niveau de risque sélectionné trouvé:', selectedRiskLevel)
      }
    }
    
    if (riskLevelToUse && riskLevelToUse.id) {
      console.log('✅ Chargement des questions avec le niveau de risque:', riskLevelToUse.id)
      RiskFormUtils.setSelectValue(select, riskLevelToUse.id)
      this.loadQuestionsWithExistingAnswers(riskLevelToUse.id)
    } else {
      console.log('⚠️ Aucun niveau de risque défini, affichage du bouton de génération')
      // Si aucun niveau de risque n'est défini, afficher le bouton "Générer le formulaire"
      // et laisser l'utilisateur sélectionner un niveau de risque
      console.log('🔧 Appel de showAccessButton(false)')
      this.showAccessButton(false)
      console.log('🔧 Appel de hideQuestionsForm()')
      this.hideQuestionsForm()
      
      // Forcer l'affichage du bouton et s'assurer qu'il est activé
      const accessButton = this.accessButtonTarget
      if (accessButton) {
        accessButton.style.display = 'inline-block'
        accessButton.disabled = false
        console.log('🔧 Bouton d\'accès forcé à l\'affichage')
      } else {
        console.log('❌ Bouton d\'accès non trouvé')
      }
      
      console.log('✅ showEditMode terminé')
    }
  }

  get hasDeleteButton() {
    return this.hasDeleteButtonTarget
  }

  // Supprimer la fiche de risque du client
  async deleteRiskForm() {
    try {
      const confirmed = window.confirm('Supprimer toutes les réponses de la fiche de risque de ce client ?')
      if (!confirmed) return

      // On peut passer le niveau de risque courant si présent
      const currentRiskLevelId = RiskFormUtils.getSelectedRiskLevel()
      const success = await this.api.deleteRiskForm(currentRiskLevelId || null)
      if (success) {
        // Nettoyer l'affichage et revenir à la sélection
        this.existingAnswersContentTarget.innerHTML = ''
        this.existingAnswersContainerTarget.style.display = 'none'
        this.showRiskSelection()
        this.hideQuestionsForm()
      } else {
        RiskFormUtils.showError('Échec de la suppression')
      }
    } catch (error) {
      RiskFormUtils.handleError(error, 'suppression de la fiche')
    }
  }

  // Exporter en PDF
  exportPdf() {
    const riskLevelId = RiskFormUtils.getSelectedRiskLevel()
    if (!riskLevelId) {
      RiskFormUtils.showError('Aucun niveau de risque sélectionné')
      return
    }
    
    const clientId = RiskFormUtils.getClientId()
    const url = `/clients/${clientId}/risk_form_pdf.pdf?risk_level_id=${riskLevelId}`
    
    // Ouvrir le PDF dans un nouvel onglet
    window.open(url, '_blank')
  }

  // Afficher la sélection du niveau de risque
  showRiskSelection() {
    console.log('👁️ Affichage de la sélection du niveau de risque')
    console.log('🎯 Target:', this.riskSelectionContainerTarget)
    this.riskSelectionContainerTarget.style.display = 'block'
    this.existingAnswersContainerTarget.style.display = 'none'
    console.log('✅ Sélection affichée')
  }

  // Charger les questions avec réponses existantes
  async loadQuestionsWithExistingAnswers(riskLevelId = null) {
    const levelId = riskLevelId || (this.state.firstRiskLevel ? this.state.firstRiskLevel.id : null)
    if (!levelId) return
    
    try {
      this.showLoading()
      const questions = await this.api.getQuestions(levelId)
      this.hideLoading()
      
      this.hideAccessButton()
      this.displayQuestions(questions)
    } catch (error) {
      this.hideLoading()
      RiskFormUtils.handleError(error, 'chargement des questions')
    }
  }

  // Gestion de l'interface
  hideQuestionsForm() {
    this.questionsContainerTarget.style.display = 'none'
    this.questionsFormTarget.innerHTML = ''
  }

  showLoading() {
    this.questionsFormTarget.innerHTML = this.renderer.renderLoading()
  }

  hideLoading() {
    // L'indicateur de chargement est remplacé par le contenu
  }
} 